import React, { useEffect, useState } from 'react';
import {
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from 'reactstrap';
import { toast } from 'react-toastify';
import SideModal from '../../components/Common/SideModal';
import { vehiclesAPI } from '../../helpers/api';

interface VehicleFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSuccess: () => void;
}

const VehicleFormModal: React.FC<VehicleFormModalProps> = ({
  isOpen,
  toggle,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    plate: '',
    brand: '',
    customBrand: '',
    model: '',
    customModel: '',
    type: 'COMPANY', // COMPANY or RENTAL
    isMachinery: false,
    ownerName: '',
    dailyRate: '',
  });

  // Brands/Models for dropdown
  const [brands, setBrands] = useState<string[]>([]);
  const [models, setModels] = useState<string[]>([]);
  const [brandModels, setBrandModels] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (isOpen) {
      resetForm();
      fetchBrandsAndModels();
    }
  }, [isOpen]);

  const resetForm = () => {
    setFormData({
      plate: '',
      brand: '',
      customBrand: '',
      model: '',
      customModel: '',
      type: 'COMPANY',
      isMachinery: false,
      ownerName: '',
      dailyRate: '',
    });
  };

  const fetchBrandsAndModels = async () => {
    try {
      const response = await vehiclesAPI.getBrandsAndModels();
      setBrands(response.data.brands);
      setModels(response.data.models);
      setBrandModels(response.data.brandModels);
    } catch (error) {
      console.error('Brands/models fetch error:', error);
    }
  };

  const getFilteredModels = () => {
    const selectedBrand = formData.brand === '__OTHER__' ? formData.customBrand : formData.brand;
    if (selectedBrand && brandModels[selectedBrand]) {
      return brandModels[selectedBrand];
    }
    return models;
  };

  // Get actual brand/model values (from dropdown or custom input)
  const getActualBrand = () => formData.brand === '__OTHER__' ? formData.customBrand : formData.brand;
  const getActualModel = () => formData.model === '__OTHER__' ? formData.customModel : formData.model;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.plate) {
      toast.error('Plaka zorunludur');
      return;
    }

    try {
      setSubmitting(true);
      const actualBrand = getActualBrand();
      const actualModel = getActualModel();

      await vehiclesAPI.create({
        plate: formData.plate.toUpperCase(),
        brand: actualBrand || undefined,
        model: actualModel || undefined,
        type: formData.type,
        isMachinery: formData.isMachinery,
        ownerName: formData.ownerName || undefined,
        dailyRate: formData.dailyRate ? parseFloat(formData.dailyRate) : undefined,
      });
      toast.success('Araç eklendi');
      onSuccess();
      toggle();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'İşlem başarısız');
    } finally {
      setSubmitting(false);
    }
  };

  const showOwnerField = formData.type !== 'COMPANY';
  const showDailyRateField = formData.type === 'RENTAL';

  return (
    <SideModal
      isOpen={isOpen}
      toggle={toggle}
      title="Yeni Araç"
      size="md"
    >
      <div style={{ margin: '-1.25rem', padding: '1rem', background: '#f3f3f9', minHeight: '100%' }}>
        <Form onSubmit={handleSubmit} className="vehicle-form">
          {/* Araç Bilgileri Card */}
          <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
            <h6 className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
              <i className="ri-truck-line me-2"></i>
              Araç Bilgileri
            </h6>
            <div>
            <FormGroup>
              <Label>Plaka *</Label>
              <Input
                type="text"
                value={formData.plate}
                onChange={(e) =>
                  setFormData({ ...formData, plate: e.target.value.toUpperCase() })
                }
                placeholder="34 ABC 123"
                required
              />
            </FormGroup>
            <FormGroup>
              <Label>Sahiplik Durumu *</Label>
              <Input
                type="select"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="COMPANY">Şirket Aracı</option>
                <option value="RENTAL">Kiralık</option>
              </Input>
            </FormGroup>
            <FormGroup check className="mb-3">
              <Input
                type="checkbox"
                id="isMachinery"
                checked={formData.isMachinery}
                onChange={(e) => setFormData({ ...formData, isMachinery: e.target.checked })}
              />
              <Label check htmlFor="isMachinery">
                <i className="ri-tools-line me-1"></i>
                İş Makinesi
              </Label>
            </FormGroup>
            <FormGroup>
              <Label>Marka</Label>
              <Input
                type="select"
                value={formData.brand}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    brand: value,
                    customBrand: value === '__OTHER__' ? formData.customBrand : '',
                    // Reset model when brand changes
                    model: '',
                    customModel: '',
                  });
                }}
              >
                <option value="">Marka Seçin</option>
                {brands.map((brand) => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
                <option value="__OTHER__">➕ Diğer (Yeni Marka)</option>
              </Input>
              {formData.brand === '__OTHER__' && (
                <Input
                  type="text"
                  value={formData.customBrand}
                  onChange={(e) => setFormData({ ...formData, customBrand: e.target.value })}
                  placeholder="Yeni marka adı girin"
                  className="mt-2"
                />
              )}
            </FormGroup>
            <FormGroup className={showOwnerField ? '' : 'mb-0'}>
              <Label>Model</Label>
              <Input
                type="select"
                value={formData.model}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({
                    ...formData,
                    model: value,
                    customModel: value === '__OTHER__' ? formData.customModel : '',
                  });
                }}
              >
                <option value="">Model Seçin</option>
                {getFilteredModels().map((model) => (
                  <option key={model} value={model}>{model}</option>
                ))}
                <option value="__OTHER__">➕ Diğer (Yeni Model)</option>
              </Input>
              {formData.model === '__OTHER__' && (
                <Input
                  type="text"
                  value={formData.customModel}
                  onChange={(e) => setFormData({ ...formData, customModel: e.target.value })}
                  placeholder="Yeni model adı girin"
                  className="mt-2"
                />
              )}
            </FormGroup>
            </div>
          </div>

          {/* Kiralama / Sahiplik Bilgileri - Şirket aracı hariç */}
          {showOwnerField && (
            <div style={{ background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderTop: '3px solid #f06548' }} className="rounded-3 p-3 mb-3">
              <h6 className="mb-3" style={{ fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                <i className="ri-money-dollar-circle-line me-2"></i>
                Kiralama Bilgileri
              </h6>
              <div>
              <FormGroup className={showDailyRateField ? '' : 'mb-0'}>
                <Label>Sahibi / Kiralama Firması</Label>
                <Input
                  type="text"
                  value={formData.ownerName}
                  onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                  placeholder="Firma veya kişi adı"
                />
              </FormGroup>
              {showDailyRateField && (
                <FormGroup className="mb-0">
                  <Label>Günlük Ücret (₺)</Label>
                  <Input
                    type="number"
                    value={formData.dailyRate}
                    onChange={(e) => setFormData({ ...formData, dailyRate: e.target.value })}
                    placeholder="Günlük kiralama ücreti"
                    min={0}
                  />
                </FormGroup>
              )}
              </div>
            </div>
          )}

          {/* Kaydet Butonu */}
          <div className="form-actions">
            <Button color="secondary" onClick={toggle} disabled={submitting}>
              İptal
            </Button>
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <i className="ri-save-line me-1"></i>
                  Kaydet
                </>
              )}
            </Button>
          </div>
        </Form>
      </div>
    </SideModal>
  );
};

export default VehicleFormModal;
